import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';

// import utils from General HTML Support plugin
import {
  getHtmlAttributeName,
  modifyGhsAttribute,
  setViewAttributes,
} from '@ckeditor/ckeditor5-html-support/src/utils';

// import utils from Heading plugin
import { getLocalizedOptions } from '@ckeditor/ckeditor5-heading/src/utils';

import TableOfContentsIcon from './table-of-contents.svg';

export default class HeadingIndex extends Plugin {
  afterInit() {
    const editor = this.editor;
    const view = editor.editing.view;
    const model = editor.editing.model;
    const mapper = editor.editing.mapper; // need for conversion from model to view

    // Get information about headings from the editor's configuration.
    const headingOptions = getLocalizedOptions(editor);
    // console.log('headingOptions', headingOptions);

    // Create a button for inserting the list.
    editor.ui.componentFactory.add('headingsList', (locale) => {
      const buttonView = new ButtonView(locale);

      buttonView.set({
        label: 'Contents',
        icon: TableOfContentsIcon,
        withText: 'Table of contents',
        tooltip: true,
      });

      // Callback executed once the button is clicked.
      buttonView.on('execute', () => {
        const root = editor.model.document.getRoot();
        const range = editor.model.createRangeIn(root);

        let headerIds = [];
        let headers = [];
        let level = [];

        // Iterate through all elements in the document and get headers.
        for (const value of range.getWalker({ ignoreElementEnd: true })) {
          // TO DO: match viewElement (h) to be able to handle all levels of heading elements
          if (value.item.is('element') && value.item.name.match(/^heading/)) {
            // Get model of the heading element
            const modelElement = value.item;
            // console.log('modelElement', modelElement);

            // Get view equivalent of the heading element
            const viewElement = mapper.toViewElement(modelElement);
            // console.log('viewElement', viewElement);

            // Get heading element text
            const textElement = modelElement.getChild(0);
            headers.push(textElement?.data || 'Unknown');

            // Get ID attribute of the heading element
            const currentID = viewElement.getAttribute('id');
            // console.log('currentID', currentID);

            if (!currentID) {
              const newID = this._uniqueID();
              headerIds.push(newID);

              editor.model.change((writer) => {
                // Get html attributes og heading element
                const GHSAttributesName = getHtmlAttributeName(
                  viewElement?.name
                );

                if (GHSAttributesName) {
                  // Modify id attribute (in htmlAttributes) at model layer
                  modifyGhsAttribute(
                    writer,
                    modelElement,
                    GHSAttributesName,
                    'attributes',
                    (attrMap) => {
                      // console.log('attrMap:', attrMap);
                      attrMap.set('id', newID);
                    }
                  );

                  // Set id attribute (in htmlAttributes) at view layer
                  view.change((writer) => {
                    setViewAttributes(
                      writer,
                      { attributes: { id: newID } },
                      viewElement
                    );
                  });
                }
              });
            } else headerIds.push(currentID);

            // Get level of header element
            level.push(
              parseInt(modelElement?.name?.replace('heading', '')) - 2
            );
            // TO DO: change to this for handle all h elements
            // level.push(parseInt(viewElement?.name?.replace('h', '')));
          }
        }

        headerIds = headerIds.reverse();
        headers = headers.reverse();
        level = level.reverse();

        // Create a new list and insert it to the document.
        model.change((writer) => {
          let i = 0;
          for (const id of headerIds) {
            const listItem = writer.createElement('listItem', {
              listType: 'numbered',
              listIndent: level[i],
            });
            writer.appendText(headers[i], { linkHref: `#${id}` }, listItem);
            writer.insert(listItem, root);
            i++;
          }
        });
      });

      return buttonView;
    });
  }

  // Generate ID based on text
  _stringToHash(string) {
    let hash = 0;
    if (string.length === 0) {
      return hash;
    }

    for (let i = 0; i < string.length; i++) {
      let char = string.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }

    return hash;
  }

  // Generate ID based on random number and date
  _uniqueID() {
    return Math.floor(Math.random() * Date.now());
  }
}
